/**
 * The screening call's status vocabulary — one copy, shared by the table and the detail panel.
 *
 * Two orthogonal facts, and the whole point of this file is that they are NOT the same fact:
 *
 *   PROGRESS  — how far the call got.   Not scheduled / Scheduled / In progress / Completed / Stopped
 *   OUTCOME   — how the call went.      Qualified / Disqualified / No answer / …
 *
 * The backend gives us one field, `lead_status`, which conflates them: "qualified" and
 * "completed-RNR" are both *finished* calls that read as outcomes, and nothing in the map says
 * "this candidate was never called at all". Both surfaces need to draw both facts, so the mapping
 * lives here rather than being re-derived — and re-diverged — in each one.
 */

import tokens from "../../theme/tokens";

const { color } = tokens;

/** lead_status -> outcome chip. Same vocabulary as the old Screening dashboard. */
export const CALL_STATUS = {
  qualified: { label: "Qualified", bg: color.successBg, fg: color.success },
  "intent-qualified": { label: "Qualified", bg: color.successBg, fg: color.success },
  disqualified: { label: "Disqualified", bg: color.dangerBg, fg: color.danger },
  "completed-RNR": { label: "No answer", bg: color.warningBg, fg: color.warning },
  in_progress: { label: "Pending", bg: color.borderRow, fg: color.textSecondary },
  dnd: { label: "Do not call", bg: color.borderRow, fg: color.textSecondary },
  paused: { label: "Paused", bg: color.borderRow, fg: color.textSecondary },
};

/**
 * Progress is never tinted by the outcome: "Completed" is indigo, not green, because a green
 * COMPLETED sitting above a red DISQUALIFIED would be two colours arguing about one candidate.
 * The outcome chip owns the good/bad signal; this one only says how far the call got.
 */
export const PROGRESS = {
  notScheduled: { label: "Not scheduled", bg: color.borderRow, fg: color.textMuted },
  scheduled: { label: "Scheduled", bg: color.borderRow, fg: color.textSecondary },
  inProgress: { label: "In progress", bg: color.warningBg, fg: color.warning },
  completed: { label: "Completed", bg: color.indigoTint, fg: color.indigo },
  stopped: { label: "Stopped", bg: color.dangerBg, fg: color.danger },
  /**
   * WE told the scheduler to stop calling. Distinct from `stopped` — and the distinction is the
   * whole point: `stopped` is FINAL (isScreened returns true for it), so folding our pause into it
   * would make every paused candidate shortlistable and un-resumable. A pause is a hold we can
   * undo; dnd is a door that closed.
   *
   * Shares the warning palette with `inProgress` because both mean "a human should look at this".
   * A paused call sends no further updates, so this chip is the only thing that will ever tell you
   * the candidate is stuck.
   */
  paused: { label: "Paused", bg: color.warningBg, fg: color.warning },
};

/**
 * dnd / paused are their own state on purpose: terminal, but not completed — that call is never
 * going to finish. Filing them under "Completed" would bury a candidate who needs a human to pick
 * up the phone.
 */
const PROGRESS_BY_LEAD_STATUS = {
  in_progress: PROGRESS.inProgress,
  qualified: PROGRESS.completed,
  "intent-qualified": PROGRESS.completed,
  disqualified: PROGRESS.completed,
  "completed-RNR": PROGRESS.completed,
  dnd: PROGRESS.stopped,
  paused: PROGRESS.stopped,
};

/**
 * Absent from the screening map means "never scheduled", not "failed to load" — the Console fetches
 * the whole job in one request (see index.js), so a missing entry is information, not a gap.
 *
 * A scheduled call whose lead_status hasn't landed yet — or is one this build has never heard of —
 * is "Scheduled": something is queued, we just can't say more. Inventing progress it may not have
 * made would be the one lie this file exists to prevent.
 */
export const screenProgress = (screening) => {
  if (!screening) return PROGRESS.notScheduled;
  // Before lead_status, not after. A paused call goes silent — the vendor sends no further
  // webhooks — so lead_status freezes at whatever it was when we hit pause, usually "in_progress".
  // Reading it would render a call that has definitively stopped as one still in flight.
  if (screening.paused) return PROGRESS.paused;
  return PROGRESS_BY_LEAD_STATUS[screening.lead_status] || PROGRESS.scheduled;
};

/**
 * Paused is a HOLD, not a result: the call was stopped before it could tell us anything, so
 * isScreened() below stays false for it. That is what keeps a paused candidate out of the
 * shortlist (which requires a finished call) and inside the set we can resume.
 */
export const isPaused = (screening) => Boolean(screening?.paused);

/**
 * Is this call still going to tell us something? Drives the console's 30s status poll.
 *
 * A paused call is SETTLED even though its lead_status reads "in_progress" — we told the scheduler
 * to stop, so no further webhook is ever coming. Without the paused check the console would poll it
 * every 30 seconds for the life of the session, waiting for an update that cannot arrive.
 */
export const isPending = (screening) =>
  !isPaused(screening) &&
  (!screening ||
    !screening.status ||
    !screening.lead_status ||
    screening.lead_status === "in_progress");

/**
 * Final = the call is never going to tell us anything more, whether it went well (Completed) or
 * was cut off (Stopped). Both are "done being screened"; neither says the candidate is any good.
 */
export const isScreened = (screening) => {
  const progress = screenProgress(screening);
  return progress === PROGRESS.completed || progress === PROGRESS.stopped;
};

/**
 * The outcome, but only once there is one. While a call is in progress the outcome chip would just
 * repeat "Pending" next to "In progress"; before it is scheduled there is nothing to report.
 */
export const screenOutcome = (screening) =>
  isScreened(screening) ? CALL_STATUS[screening?.lead_status] || null : null;
